// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getUnitListPrice } from "@awc/App/Services/Helpers/ProductPriceService";
import { xIsAuthenticated } from "@awc/App/Store/Selectors/GeneralSelectors";
import { mergeWithCss } from "@awc/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useMemo } from "react";

interface OwnProps {
    extendedStyles?: ProductSavePriceStyles;
}

type Props = OwnProps & HasProduct;

export interface ProductSavePriceStyles {
    wrapper?: InjectableCss;
    realtimePrice?: TypographyPresentationProps;
    savePrice?: TypographyPresentationProps;
    originalPrice?: TypographyPresentationProps;
}

export const productSavePriceStyles: ProductSavePriceStyles = {};

const ProductSavePrice: FC<Props> = ({ product, productInfo, extendedStyles }) => {
    const styles = useMemo(() => mergeWithCss(productSavePriceStyles, extendedStyles), []);

    const { showPricing, isAuth } = useAppSelector(state => {
        return {
            showPricing: state.context.showPricing,
            isAuth: xIsAuthenticated(state),
        };
    });

    const pricing = productInfo.pricing;
    if (!pricing) {
        return null;
    }

    const qtyOrdered = productInfo.qtyOrdered;

    const { price: unitListPrice, priceDisplay: unitListPriceDisplay } = getUnitListPrice(pricing, qtyOrdered || 1);

    const quoteRequired = product.quoteRequired || pricing?.unitNetPrice === 0;

    // savedPrice = basic list price - realTime price (basic list price can be added from admin)
    const basicListPrice = product.unitListPrice;
    const savedPrice = basicListPrice - unitListPrice;
    if (!showPricing || productInfo.failedToLoadPricing || !isAuth || savedPrice <= 0 || quoteRequired) {
        return null;
    }

    const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.realtimePrice}>{unitListPriceDisplay}</Typography>
            <Typography {...styles.savePrice}>{translate(`Save ${formatter.format(savedPrice)}`)}</Typography>
            <Typography {...styles.originalPrice}>{translate(`Was ${formatter.format(basicListPrice)}`)}</Typography>
        </StyledWrapper>
    );
};

export default withProduct(ProductSavePrice);
